import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import HTMLFlipBook from 'react-pageflip';
import { useTranslation } from 'react-i18next';

import { CategoryData, CategoryDictionary, LearnedWord, WordData } from '../../types/types';
import { CrossReference, extractCrossReferences, extractGardinerCodes, resolveSignExample } from '../../utils/hieroglyphReading';
import { displayCategoryLabel } from '../../utils/categoryLabels';
import { displayKamaChapterTitle } from '../../utils/kamaChapterTitles';
import FloatingBackButton from '../FloatingBackButton';
import HieroglyphCard from './HieroglyphCard';
import KamaWordTest from './KamaWordTest';
import './KamaLearnMode.css';

interface KamaLearnModeProps {
  categoryLabel: string;
  categoryData: CategoryData;
  categories: CategoryDictionary;
  onExit?: () => void;
  // The trail step right after this lesson (CategorySelection) — offered on
  // the done page once the lesson is complete, so the reader moves straight
  // on instead of going back to the trail to find it.
  nextLesson?: { label: string; onStart: () => void };
  // Reports the words to credit once a batch of the lesson is over — every
  // non-header word immediately for purely narrative/methodology categories
  // (nothing to test), or only the words actually mastered in that batch's
  // post-browse test otherwise (see `isTestableWord`). Fired once per
  // WORDS_PER_TEST_BATCH-sized chunk (see below), not just at the very end,
  // mirroring LearnMode.tsx's own per-batch onWordsLearned crediting.
  // `isFinalSegment` tells the caller whether this is the category's last
  // batch — App.tsx only marks the category "completed" (sync + celebration)
  // on that final call, since doing so on every batch would interrupt the
  // lesson far more often than intended.
  onLessonComplete?: (category: string, words: LearnedWord[], isFinalSegment?: boolean) => void;
  // Per-quiz-card answer (word.quiz entries only) — same signal QuizComponent/
  // ExerciseMode feed into handleQuizAnswer for the running answer streak.
  onAnswer?: (correct: boolean) => void;
  // Excludes narrative/methodology entries (alphabet-adjacent story cards,
  // whole categories like "Lecture analytique"/"Phrase") from the post-browse
  // test — App.tsx supplies the real predicate built from its existing
  // KAMA_NON_VOCAB_* exclusion sets. Defaults to "everything is testable" so
  // this prop is optional for any other caller.
  isTestableWord?: (key: string) => boolean;
}

interface ExternalWordView {
  key: string;
  word: WordData;
  categoryLabel: string;
}

// Simple fade/scale-in for the detached cross-reference detour card only —
// the real page-turn feel below is handled entirely by react-pageflip.
const detourVariants = {
  enter: { opacity: 0, scale: 0.97 },
  center: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.97 },
};

// react-pageflip's TypeScript definitions mark every IFlipSetting field as
// required, even though the underlying library applies sensible defaults at
// runtime — spelled out once here so <HTMLFlipBook> doesn't need ~20 inline
// props below, and so each choice is deliberate rather than accidental.
const FLIPBOOK_SETTINGS = {
  startPage: 0,
  size: 'fixed' as const,
  width: 320,
  // Tall enough for the longest text card (intro cards, Pharaons/Villes
  // readings) so .kama-page never falls back to its own internal scroll —
  // that scroll sat inside the overlay's, showing two scrollbars and
  // trapping touch scrolling. Shorter cards don't pay for it, since
  // .kama-lesson__card-clip shrinks to the page's real height; the overlay
  // is then the only scroll container. (size:'fixed' ignores min/maxHeight.)
  // Mirrored in KamaLearnMode.css (.kama-flipbook .stf__wrapper) — keep both
  // in sync.
  height: 2400,
  minWidth: 280,
  maxWidth: 420,
  minHeight: 360,
  maxHeight: 640,
  drawShadow: true,
  flippingTime: 500,
  usePortrait: true,
  startZIndex: 0,
  autoSize: true,
  maxShadowOpacity: 0.5,
  showCover: false,
  mobileScrollSupport: true,
  clickEventForward: true,
  // Both useMouseEvents:false and disableFlipByClick:true were tried to stop
  // clicks on the book's empty dead-zone (see .kama-page comment below) from
  // turning the page — both also silently broke the imperative
  // flipNext()/flipPrev() ref calls the Suivant/Précédent buttons rely on
  // (this library ties its programmatic API to the same interaction gating
  // as direct user input, not just user-driven clicks/drags). Left at
  // defaults here; the dead-zone itself is fixed in KamaLearnMode.css
  // instead (.kama-page top-aligned + book height trimmed), which doesn't
  // touch this interaction plumbing at all.
  useMouseEvents: true,
  swipeDistance: 30,
  // Verified in page-flip's own source (userMove(), page-flip.browser.js):
  // this only toggles the on-hover corner-fold hint, a code path separate
  // from both the click-to-flip handler and the imperative flipNext()/
  // flipPrev() API — unlike useMouseEvents/disableFlipByClick above, this
  // one is safe to turn off. Kills the stray page-corner graphic that was
  // floating below the visible card content (the dead-zone below a short
  // card, per the .kama-page comment).
  showPageCorners: false,
  disableFlipByClick: false,
};

// Above this many entries the header's progress bar is drawn as one
// continuous track instead of one segment per card (19d's segmented bar
// suits the 32-entry alphabet chapter, not a 150-word vocabulary category).
const MAX_PROGRESS_SEGMENTS = 40;

// Same batch size as LearnMode.tsx's WORDS_PER_LESSON — the test now
// interrupts browsing every N real vocabulary words instead of only once at
// the end of the whole category.
const WORDS_PER_TEST_BATCH = 5;

// Categories split into chapters (isSectionHeader cards — "Lire les
// hiéroglyphes") test at chapter ends instead of every N words, so a test
// never cuts a chapter in two (n tested with the lips, r with the h's).
// Chapters too small to deserve their own test (n · r, s · š) roll into the
// next one until at least this many words have been seen.
const MIN_CHAPTER_TEST_WORDS = 3;

interface LessonSegment {
  entries: [string, WordData][];
  vocab: [string, WordData][];
}

// Standalone lesson flow for Kama, built as a sibling to LearnMode.tsx rather
// than a branch inside it — Kama's data shape (Gardiner codes, cross-fiche
// citations embedded in `reading`) doesn't fit LearnMode's existing rendering
// path, and this keeps that shared, many-language component untouched.
const KamaLearnMode: React.FC<KamaLearnModeProps> = ({
  categoryLabel,
  categoryData,
  categories,
  onExit,
  nextLesson,
  onLessonComplete,
  onAnswer,
  isTestableWord = () => true,
}) => {
  const { t, i18n } = useTranslation();
  const entries = useMemo(() => Object.entries(categoryData), [categoryData]);
  // Splits the category's entries into consecutive segments, each holding up
  // to WORDS_PER_TEST_BATCH real vocabulary words (plus whatever narrative/
  // header entries fall alongside them) — the book only shows one segment at
  // a time, and each segment's "done" page offers its own test rather than
  // one big test at the very end. Purely narrative/methodology categories
  // (e.g. "Lecture analytique", "Phrase") never reach the batch size, so they
  // naturally collapse to a single segment — unchanged from before.
  const segments = useMemo<LessonSegment[]>(() => {
    const segs: LessonSegment[] = [];
    let curEntries: [string, WordData][] = [];
    let curVocab: [string, WordData][] = [];
    const byChapter = entries.some(([, word]) => word.isSectionHeader);
    entries.forEach(([key, word]) => {
      if (byChapter && word.isSectionHeader && curVocab.length >= MIN_CHAPTER_TEST_WORDS) {
        segs.push({ entries: curEntries, vocab: curVocab });
        curEntries = [];
        curVocab = [];
      }
      curEntries.push([key, word]);
      if (!word.isSectionHeader && isTestableWord(key)) {
        curVocab.push([key, word]);
        if (!byChapter && curVocab.length === WORDS_PER_TEST_BATCH) {
          segs.push({ entries: curEntries, vocab: curVocab });
          curEntries = [];
          curVocab = [];
        }
      }
    });
    if (curEntries.length > 0) {
      // A slice ending on a full chapter (e.g. part 1 of "Lire les
      // hiéroglyphes" ends on q · k · g) keeps its own test.
      const trailingIsFullChapter = byChapter && curVocab.length >= MIN_CHAPTER_TEST_WORDS;
      if (segs.length > 0 && !trailingIsFullChapter) {
        // Fold a trailing partial batch into the previous segment instead
        // of giving it its own near-empty final segment — e.g. a 26-word
        // category would otherwise end 5,5,5,5,5,1: that lone last card is
        // simultaneously the first AND last card of its own segment, so
        // the "Terminer" nav button appears on it immediately, reading as
        // the lesson abruptly ending after only 6 cards. Merging keeps
        // segments flowing smoothly (5,5,5,5,6) all the way to the real
        // end of the category.
        const last = segs[segs.length - 1];
        segs[segs.length - 1] = {
          entries: [...last.entries, ...curEntries],
          vocab: [...last.vocab, ...curVocab],
        };
      } else {
        segs.push({ entries: curEntries, vocab: curVocab });
      }
    }
    return segs.length > 0 ? segs : [{ entries: [], vocab: [] }];
  }, [entries, isTestableWord]);
  // Running count of entries in every segment before this one — lets the
  // progress bar and chapter badge below keep reporting position across the
  // *whole* category rather than resetting per segment.
  const segmentOffsets = useMemo(() => {
    const offsets: number[] = [];
    let acc = 0;
    segments.forEach(seg => { offsets.push(acc); acc += seg.entries.length; });
    return offsets;
  }, [segments]);

  // Mirrors LearnMode.tsx's own `${lang}-${cat}-inProgress` localStorage
  // session (see LearnMode.tsx's loadNextWords/the mode==='preview' save
  // effect): resume at the exact card the reader left off on, not just the
  // segment, and only if the session is less than 24h old — otherwise
  // quitting mid-category (KamaCategoryPreview unmounts entirely on
  // "Quitter", there's no other state to resume from) always restarted
  // browsing at card 1, discarding whatever batches were already tested.
  // Keyed by category + first-word/length, since a split category's parts
  // (e.g. "Famille" part 1 vs part 2) share the same categoryLabel and only
  // their word slice differs.
  const progressStorageKey = `ka-kama-segment-progress-${categoryLabel}-${entries[0]?.[0] ?? ''}-${entries.length}`;
  const PROGRESS_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  const readSavedProgress = (): { segmentIndex: number; index: number } | null => {
    try {
      const raw = localStorage.getItem(progressStorageKey);
      if (!raw) return null;
      const saved = JSON.parse(raw) as { segmentIndex?: number; index?: number; timestamp?: number };
      if (!saved.timestamp || Date.now() - saved.timestamp > PROGRESS_MAX_AGE_MS) return null;
      if (typeof saved.segmentIndex !== 'number' || saved.segmentIndex < 0 || saved.segmentIndex >= segments.length) return null;
      const seg = segments[saved.segmentIndex];
      if (typeof saved.index !== 'number' || saved.index < 0 || saved.index > seg.entries.length) return null;
      return { segmentIndex: saved.segmentIndex, index: saved.index };
    } catch { return null; }
  };

  const [segmentIndex, setSegmentIndex] = useState(() => readSavedProgress()?.segmentIndex ?? 0);
  const currentSegment = segments[Math.min(segmentIndex, segments.length - 1)];
  const isFinalSegment = segmentIndex >= segments.length - 1;

  const [index, setIndex] = useState(() => readSavedProgress()?.index ?? 0);

  // Re-saved on every card flip (not just on a segment change), same as
  // LearnMode's own per-card save while in 'preview' mode.
  useEffect(() => {
    try { localStorage.setItem(progressStorageKey, JSON.stringify({ segmentIndex, index, timestamp: Date.now() })); } catch {}
  }, [progressStorageKey, segmentIndex, index]);

  // Completing a non-final segment's test moves on to the next segment,
  // which is its own visibly different screen (a fresh card) — nothing
  // extra needs to signal "this worked". The *final* segment has nowhere
  // left to advance to, so without this flag its "done" page would look
  // completely unchanged after the test (still offering "Commencer le
  // test", same "X mots parcourus" text) — indistinguishable from having
  // never taken the test at all. This tracks that the final segment's test
  // specifically has already been passed, so the done page can show that.
  const [finalSegmentTestPassed, setFinalSegmentTestPassed] = useState(false);
  const [testing, setTesting] = useState(false);
  // Guards against re-firing onLessonComplete on every re-render once the
  // book has reached its "done" page, and resets when the category itself
  // changes (browsing to a different node is a new lesson).
  const reportedRef = useRef<string | null>(null);
  // react-pageflip's imperative handle — `.pageFlip()` exposes flipNext /
  // flipPrev / turnToPage, untyped by the library itself (ref type is `any`
  // in its own .d.ts), so this stays `any` too rather than pretending to a
  // stronger type that doesn't exist upstream.
  const bookRef = useRef<any>(null);
  // A cross-reference points into a different category than the one being
  // browsed here, so it's shown as a detached single-card detour rather than
  // a real page in the book — with its own way back to where you were.
  const [externalWord, setExternalWord] = useState<ExternalWordView | null>(null);

  const isDone = index >= currentSegment.entries.length;
  // This lesson has nothing left: last segment, and either its test is
  // passed or it never had one (no testable vocabulary — "Lire les
  // hiéroglyphes", "Phrases"…, which used to still say "passe au test pour
  // terminer" with no test button anywhere).
  const isLessonComplete = isFinalSegment
    && (currentSegment.vocab.length === 0 || finalSegmentTestPassed);
  const current = currentSegment.entries[index];
  // Position across the *whole* category (not just the current segment) —
  // feeds the chapter badge below so it keeps counting up smoothly as the
  // reader moves from one segment's test into the next. Only valid while a
  // real card is showing (index < segment length) — on the "done" page,
  // `current` is undefined and chapterInfo bails out before this matters.
  const globalIndex = segmentOffsets[segmentIndex] + index;
  // "Cards actually seen" for the progress bar/counter — deliberately NOT
  // globalIndex + 1: on the segment's "done" page, `index` overshoots to
  // currentSegment.entries.length (one past the last real card), so without
  // clamping to the segment's own size first, the counter would jump an
  // extra step ahead (e.g. showing 6/20 right after only 5 words were
  // browsed) instead of holding at the segment's real total until the next
  // segment actually starts.
  const globalCardsSeen = segmentOffsets[segmentIndex] + Math.min(index + 1, currentSegment.entries.length);

  // react-pageflip needs every page to share the same fixed height (380px,
  // see FLIPBOOK_SETTINGS) for its flip animation, but most cards are far
  // shorter than that — left uncorrected, the leftover space inside the
  // book reads as a large dead gap between the visible card and the
  // Suivant/Précédent row below it. Rather than touch the book's own fixed
  // geometry, a wrapping div around it is clipped to the CURRENT page's
  // real content height, capped at the book's fixed height so genuinely
  // tall cards keep relying on .kama-page's own internal scroll instead of
  // growing the page past its flip-animation bounds.
  //
  // react-pageflip inserts its page DOM nodes itself, outside normal React
  // commit timing (it does so after this component's own mount effects have
  // already run), so an index-keyed useEffect reading the refs would
  // permanently see `null` for the very first page — measuring is done from
  // the ref callback itself instead, which only ever fires once the node
  // genuinely exists, plus a persistent ResizeObserver per page (so an
  // expanding "Détails" panel on the currently active page keeps updating
  // it live).
  // .hgw__card carries a box-shadow (see HieroglyphCard.css's --shadow-md)
  // that paints below its border without adding to layout height/scrollHeight
  // — clipping exactly at scrollHeight sliced that shadow off flat, making
  // the card's bottom edge look abruptly cut instead of fading out. Padding
  // the clip height by the shadow's reach leaves it room to render.
  const SHADOW_CLIP_BUFFER = 20;
  const clipHeightFor = (el: HTMLDivElement) =>
    Math.min(el.scrollHeight + SHADOW_CLIP_BUFFER, FLIPBOOK_SETTINGS.height);

  const pageContentRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cardClipRef = useRef<HTMLDivElement | null>(null);
  const indexRef = useRef(index);
  indexRef.current = index;
  const [cardSlotHeight, setCardSlotHeight] = useState<number | undefined>(undefined);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  if (!resizeObserverRef.current) {
    resizeObserverRef.current = new ResizeObserver((observed) => {
      for (const entry of observed) {
        const i = pageContentRefs.current.indexOf(entry.target as HTMLDivElement);
        if (i === indexRef.current) {
          setCardSlotHeight(clipHeightFor(entry.target as HTMLDivElement));
        }
      }
    });
  }
  useEffect(() => () => resizeObserverRef.current?.disconnect(), []);
  // Covers switching to a page whose node was already attached earlier (the
  // ResizeObserver above only fires on a *change* in size, not on becoming
  // active) — reads the height synchronously from the already-mounted node.
  useEffect(() => {
    const el = pageContentRefs.current[index];
    if (el) setCardSlotHeight(clipHeightFor(el));
    // A long card is now scrolled by the overlay itself (see
    // FLIPBOOK_SETTINGS.height) — start the next card from its top instead
    // of wherever the previous one was scrolled to.
    cardClipRef.current?.closest('.kama-category-preview-overlay')?.scrollTo({ top: 0 });
  }, [index]);
  const setPageContentRef = (i: number) => (el: HTMLDivElement | null) => {
    const prev = pageContentRefs.current[i];
    if (prev && prev !== el) resizeObserverRef.current?.unobserve(prev);
    pageContentRefs.current[i] = el;
    if (el) {
      resizeObserverRef.current?.observe(el);
      if (i === indexRef.current) setCardSlotHeight(clipHeightFor(el));
    }
  };

  // Only auto-credits completion here when this segment has no real
  // vocabulary to test (purely narrative/methodology categories always fall
  // into this case, see the single-segment collapse above) — otherwise the
  // "done" page offers "Commencer le test" instead, and onLessonComplete only
  // fires once that segment's test reports which words were mastered.
  useEffect(() => {
    if (!isDone || !onLessonComplete || currentSegment.vocab.length > 0) return;
    const reportKey = `${categoryLabel}-${segmentIndex}`;
    if (reportedRef.current === reportKey) return;
    reportedRef.current = reportKey;
    const words: LearnedWord[] = currentSegment.entries
      .filter(([, word]) => !word.isSectionHeader)
      .map(([key, word]) => ({
        word: key,
        category: categoryLabel,
        language: 'ka' as LearnedWord['language'],
        timestamp: Date.now(),
        translation: word.translation || '',
      }));
    onLessonComplete(categoryLabel, words, isFinalSegment);
    if (!isFinalSegment) {
      setSegmentIndex(i => i + 1);
      setIndex(0);
    } else {
      // The whole category is done — clear the resume point so a future
      // visit (e.g. to review) starts fresh from segment 0 instead of
      // reopening straight onto the last segment.
      try { localStorage.removeItem(progressStorageKey); } catch {}
    }
  }, [isDone, categoryLabel, segmentIndex, currentSegment, isFinalSegment, onLessonComplete, progressStorageKey]);

  // A category switch (browsing to a different node, while this component
  // instance stays mounted) should never resume mid-test from the previous
  // one, and always restarts from the category's first segment — but this
  // must NOT fire on the initial mount too, or it would immediately stomp the
  // resumed segmentIndex the lazy useState initializer above just restored.
  // A one-shot boolean ref ("have I run once yet?") looks like the obvious
  // guard here, but it isn't safe: React 18 StrictMode (dev only) deliberately
  // double-invokes effects on every mount (mount → simulated unmount →
  // simulated remount, same effect body called twice), which flips a one-shot
  // flag to "already ran" after the first of those two calls — so the SECOND
  // call sees "not first anymore" and fires the reset right after mount,
  // wiping out the just-restored segmentIndex. Comparing against the actual
  // previous categoryLabel instead of a boolean is idempotent across that
  // double-invoke: as long as categoryLabel hasn't truly changed, both calls
  // see the ref still equal to the current value and skip the reset.
  const prevCategoryLabelRef = useRef(categoryLabel);
  useEffect(() => {
    if (prevCategoryLabelRef.current === categoryLabel) return;
    prevCategoryLabelRef.current = categoryLabel;
    setTesting(false);
    setSegmentIndex(0);
    setIndex(0);
    setFinalSegmentTestPassed(false);
  }, [categoryLabel]);

  const externalGardinerSigns = useMemo(
    () => (externalWord ? extractGardinerCodes(externalWord.word.reading, i18n.language) : []),
    [externalWord, i18n.language],
  );
  const externalCrossReferences = useMemo(
    () => (externalWord ? extractCrossReferences(externalWord.word.reading, categories) : []),
    [externalWord, categories],
  );

  // Chapters = runs of entries following an isSectionHeader card (see
  // lecture.ts's alphabet groupings). Computed over the FULL category
  // (categories[categoryLabel]), not just `entries`: a long category opens
  // as one SPLIT_SIZE slice at a time (CategorySelection.tsx — e.g. "Lire
  // les hiéroglyphes · 2"), and numbering chapters within the slice alone
  // showed "CHAPITRE 1 / 1" on part 2 instead of its real place in the
  // whole path. Indices below are FULL-category indices; `toFull[i]` maps a
  // slice index to its full index. `chapterOf[i]` (slice-indexed) is the
  // full index of the header opening entry i's chapter (-1 before any
  // header, and for every entry of categories with no headers — Famille,
  // Corps, ...).
  const chapters = useMemo(() => {
    const full = categories[categoryLabel];
    const fullEntries = full
      ? (Object.entries(full).filter(([key]) => !key.includes('_')) as [string, WordData][])
      : entries;
    const fullIndexByKey = new Map(fullEntries.map(([key], i) => [key, i]));
    // A slice key missing from the full category (shouldn't happen) would
    // make every lookup below fail — fall back to the slice itself then.
    const usable = entries.every(([key]) => fullIndexByKey.has(key));
    const source = usable ? fullEntries : entries;
    const toFull = usable ? entries.map(([key]) => fullIndexByKey.get(key)!) : entries.map((_, i) => i);

    const headers: number[] = [];
    const fullChapterOf: number[] = [];
    let currentHeader = -1;
    source.forEach(([, w], i) => {
      if (w.isSectionHeader) {
        currentHeader = i;
        headers.push(i);
      }
      fullChapterOf.push(currentHeader);
    });
    const membersOf = (header: number) =>
      source.flatMap(([, w], i) => (
        fullChapterOf[i] === header && !w.isSectionHeader && !w.isChapterReview ? [i] : []
      ));
    return { headers, chapterOf: toFull.map((i) => fullChapterOf[i]), toFull, membersOf, source };
  }, [categories, categoryLabel, entries]);

  // react-pageflip mounts every page up front (it manages show/hide via its
  // own transforms) instead of the old single-current-card swap, so the
  // per-page extras are computed once for the whole segment rather than
  // scoped to just the visible entry.
  const pageExtras = useMemo(
    () => currentSegment.entries.map(([, word]) => ({
      gardinerSigns: extractGardinerCodes(word.reading, i18n.language),
      crossReferences: extractCrossReferences(word.reading, categories),
      example: resolveSignExample(word.signExample, categories) ?? undefined,
    })),
    [currentSegment, categories, i18n.language],
  );

  // Chapter line of the header band ("KAMA · CHAPITRE 1 / 6", then the
  // chapter title and "SIGNE 3 / 5") — gives a sense of place while flipping
  // through a long run of otherwise-identical reference cards. `position` is
  // 0 on the chapter's own header card.
  const chapterInfo = useMemo(() => {
    if (!current) return null;
    const header = chapters.chapterOf[globalIndex];
    if (header < 0) return null;
    const members = chapters.membersOf(header);
    return {
      chapterNum: chapters.headers.indexOf(header) + 1,
      total: chapters.headers.length,
      title: chapters.source[header][0],
      position: members.indexOf(chapters.toFull[globalIndex]) + 1,
      count: members.length,
    };
  }, [chapters, globalIndex, current]);

  // Ibis-guided cards (`word.guide`, KamaGuideCard) are read bubble by
  // bubble with the same Suivant/Précédent as the book — one button, not a
  // second « Continuer » inside the card competing with it. Only past the
  // last bubble (or before the first) does the page actually turn. Keyed by
  // entry key so going back to a card finds it where it was left.
  const [guideSteps, setGuideSteps] = useState<Record<string, number>>({});
  const currentEntry = currentSegment.entries[index];
  const currentGuideSteps = currentEntry?.[1].guide?.steps.length ?? 0;
  const currentGuideStep = currentEntry ? guideSteps[currentEntry[0]] ?? 0 : 0;
  const setGuideStep = (key: string, step: number) =>
    setGuideSteps((prev) => ({ ...prev, [key]: step }));
  const goNext = () => {
    if (currentEntry && currentGuideStep < currentGuideSteps - 1) {
      setGuideStep(currentEntry[0], currentGuideStep + 1);
      return;
    }
    bookRef.current?.pageFlip().flipNext();
  };
  const goPrev = () => {
    if (currentEntry && currentGuideStep > 0) {
      setGuideStep(currentEntry[0], currentGuideStep - 1);
      return;
    }
    bookRef.current?.pageFlip().flipPrev();
  };
  const restart = () => bookRef.current?.pageFlip().turnToPage(0);
  const handleFlip = (e: any) => setIndex(e.data);

  const navigateToReference = (ref: CrossReference) => {
    if (!ref.word || !ref.resolvedCategoryKey) return;
    setExternalWord({ key: ref.citedKey, word: ref.word, categoryLabel: ref.resolvedCategoryKey });
  };

  if (testing) {
    return (
      <KamaWordTest
        categoryLabel={categoryLabel}
        words={currentSegment.vocab}
        fullCategoryData={categoryData}
        onExit={onExit}
        onComplete={(masteredWords) => {
          setTesting(false);
          onLessonComplete?.(categoryLabel, masteredWords, isFinalSegment);
          if (!isFinalSegment) {
            setSegmentIndex(i => i + 1);
            setIndex(0);
          } else {
            setFinalSegmentTestPassed(true);
            try { localStorage.removeItem(progressStorageKey); } catch {}
          }
        }}
      />
    );
  }

  if (externalWord) {
    return (
      <div className="kama-lesson">
        <div className="kama-lesson__header">
          <span className="kama-lesson__category">{displayCategoryLabel(externalWord.categoryLabel, t)}</span>
        </div>
        {onExit && <FloatingBackButton onBack={onExit} position="top-left" variant="minimal" />}

        <p className="kama-lesson__xref-note">{t('kamaLearnMode.crossReferenceNote', { category: displayCategoryLabel(categoryLabel, t) })}</p>

        <div className="kama-lesson__card-slot">
          <AnimatePresence mode="wait">
            <motion.div
              key={externalWord.key}
              variants={detourVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22, ease: 'easeOut' }}
              style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <HieroglyphCard
                entryKey={externalWord.key}
                word={externalWord.word}
                gardinerSigns={externalGardinerSigns}
                crossReferences={externalCrossReferences}
                onNavigate={navigateToReference}
                onAnswered={onAnswer}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        <button
          type="button"
          className="kama-lesson__nav-btn kama-lesson__nav-btn--primary"
          onClick={() => setExternalWord(null)}
        >
          {t('kamaLearnMode.backToCategory', { category: displayCategoryLabel(categoryLabel, t) })}
        </button>
      </div>
    );
  }

  const cardsSeen = Math.min(globalCardsSeen, entries.length);
  const currentIsSign = Array.from(current?.[1].illustration ?? '').length === 1;

  return (
    <div className="kama-lesson">
      {/* Crenellated header band (design 19d): back, chapter + category,
          card counter, then the progress bar split into chapters. */}
      <div className="kama-band-wrap">
        <header className="kama-band">
          <div className="kama-band__top">
            {onExit ? (
              <button type="button" className="kama-band__back" onClick={onExit} aria-label={t('kamaLearnMode.quit')}>
                <span className="kama-chevron" />
              </button>
            ) : (
              <span className="kama-band__back kama-band__back--placeholder" />
            )}
            <div className="kama-band__titles">
              <span className="kama-band__kicker">
                {chapterInfo
                  ? t('kamaLearnMode.bandKickerChapter', { num: chapterInfo.chapterNum, total: chapterInfo.total })
                  : t('kamaLearnMode.bandKicker')}
              </span>
              <span className="kama-band__title">{displayCategoryLabel(categoryLabel, t)}</span>
            </div>
            <span className="kama-band__count">{cardsSeen}/{entries.length}</span>
          </div>

          {entries.length <= MAX_PROGRESS_SEGMENTS ? (
            <div className="kama-band__segments" aria-hidden="true">
              {entries.map(([key], i) => (
                <span
                  key={key}
                  className={[
                    'kama-band__segment',
                    i < cardsSeen ? 'is-done' : '',
                    entries[i + 1]?.[1].isSectionHeader ? 'is-chapter-end' : '',
                  ].filter(Boolean).join(' ')}
                />
              ))}
            </div>
          ) : (
            <div className="kama-band__track" aria-hidden="true">
              <span className="kama-band__fill" style={{ width: `${(cardsSeen / entries.length) * 100}%` }} />
            </div>
          )}

          {chapterInfo && (
            <div className="kama-band__meta">
              <span>{displayKamaChapterTitle(chapterInfo.title, t)}</span>
              {chapterInfo.position > 0 && (
                <span>
                  {t(currentIsSign ? 'kamaLearnMode.signPosition' : 'kamaLearnMode.cardPosition', {
                    num: chapterInfo.position,
                    total: chapterInfo.count,
                  })}
                </span>
              )}
            </div>
          )}
        </header>
        <div className="kama-band__crenels" aria-hidden="true">
          {Array.from({ length: 8 }, (_, i) => <span key={i} />)}
        </div>
      </div>

      <div
        className="kama-lesson__card-clip"
        ref={cardClipRef}
        style={{ height: cardSlotHeight ?? FLIPBOOK_SETTINGS.height }}
      >
        <div className="kama-lesson__card-slot kama-lesson__card-slot--book">
          <HTMLFlipBook
            {...FLIPBOOK_SETTINGS}
            // Forces a full remount when moving to the next segment (its page
            // count/content differs from the previous one) — without it,
            // react-pageflip would keep the old segment's internal DOM/page
            // count instead of picking up the new one.
            key={segmentIndex}
            // react-pageflip only reads startPage at mount time. This component
            // unmounts <HTMLFlipBook> whenever a cross-reference detour is open
            // (see the `externalWord` early-return above) and remounts it on
            // return, which without this override would always reopen at page 0
            // — losing the reader's place — even though `index` itself was never
            // reset. Passing the live index makes the remount resume correctly.
            startPage={index}
            className="kama-flipbook"
            style={{}}
            ref={bookRef}
            onFlip={handleFlip}
          >
            {currentSegment.entries.map(([key, word], i) => (
              <div className="kama-page" key={key}>
                <div className="kama-page__content" ref={setPageContentRef(i)}>
                  <HieroglyphCard
                    entryKey={key}
                    word={word}
                    gardinerSigns={pageExtras[i].gardinerSigns}
                    crossReferences={pageExtras[i].crossReferences}
                    example={pageExtras[i].example}
                    onNavigate={navigateToReference}
                    onAnswered={onAnswer}
                    guideStep={guideSteps[key] ?? 0}
                    onGuideStepChange={(step) => setGuideStep(key, step)}
                  />
                </div>
              </div>
            ))}
            <div className="kama-page kama-page--done" key="__kama_done__">
              {/* Registered at index currentSegment.entries.length (the same
                  index `isDone` matches on), so this page's real height feeds
                  the same card-clip measurement as every other page above —
                  without it, the clip stays sized to whatever the last
                  browsed card measured and this content renders past the
                  visible, clipped area (present in the DOM, invisible on
                  screen). */}
              <div className="kama-page__content" ref={setPageContentRef(currentSegment.entries.length)}>
                <div className="kama-lesson__done">
                  {/* Mounted only once the done page is actually on screen —
                      react-pageflip mounts every page up front, so a
                      permanently-rendered animation would have already
                      played out unseen by the time the reader got here. */}
                  {isDone && isLessonComplete && (
                    <div className="kama-success" aria-hidden="true">
                      <span className="kama-success__rays" />
                      <span className="kama-success__disc">
                        <span className="kama-success__ankh">☥</span>
                      </span>
                      {['𓋹', '𓇳', '𓆣', '𓋹', '𓇳', '𓆣'].map((glyph, i) => (
                        <span key={i} className={`kama-success__spark kama-success__spark--${i}`}>{glyph}</span>
                      ))}
                    </div>
                  )}
                  {isLessonComplete && (
                    <p className="kama-lesson__done-title">{t('kamaLearnMode.doneTitle')}</p>
                  )}
                  <p className="kama-lesson__done-text">
                    {isFinalSegment
                      ? (isLessonComplete
                          ? t('kamaLearnMode.wordsDoneFinished', { count: entries.length, category: displayCategoryLabel(categoryLabel, t) })
                          : t('kamaLearnMode.wordsDoneLastBatch', { count: entries.length }))
                      : t('kamaLearnMode.wordsDoneContinue', { count: currentSegment.entries.length })}
                  </p>
                  {/* Once the final segment's test has been passed there's no
                      "Commencer le test" left to offer — showing it again
                      here would look exactly like the pre-test screen, as if
                      the test hadn't registered at all. */}
                  {currentSegment.vocab.length > 0 && !(isFinalSegment && finalSegmentTestPassed) ? (
                    <>
                      <button
                        type="button"
                        className="kama-lesson__nav-btn kama-lesson__nav-btn--primary"
                        onClick={() => setTesting(true)}
                      >
                        {t('kamaLearnMode.startTest')}
                      </button>
                      <button
                        type="button"
                        className="kama-lesson__exit"
                        onClick={restart}
                      >
                        {t('kamaLearnMode.reviewCards')}
                      </button>
                    </>
                  ) : (
                    // The path genuinely has nothing left to offer here — the
                    // test (if any) is already passed, so "Recommencer" used
                    // to be the only button, wrongly implying there was still
                    // something to redo. Sends the reader back to the
                    // category list instead; falls back to "Recommencer"
                    // only when no onExit was supplied (e.g. the standalone
                    // HieroglyphCardPreview harness), so the button is never
                    // a dead click.
                    isLessonComplete && nextLesson ? (
                      <>
                        <button
                          type="button"
                          className="kama-lesson__nav-btn kama-lesson__nav-btn--primary"
                          onClick={nextLesson.onStart}
                        >
                          {t('kamaLearnMode.nextLesson')}
                        </button>
                        <p className="kama-lesson__next-label">{nextLesson.label}</p>
                        {onExit && (
                          <button type="button" className="kama-lesson__exit" onClick={onExit}>
                            {t('kamaLearnMode.backToCategories')}
                          </button>
                        )}
                      </>
                    ) : onExit ? (
                      <button
                        type="button"
                        className="kama-lesson__nav-btn kama-lesson__nav-btn--primary"
                        onClick={onExit}
                      >
                        {t('kamaLearnMode.backToCategories')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="kama-lesson__nav-btn kama-lesson__nav-btn--primary"
                        onClick={restart}
                      >
                        {t('kamaLearnMode.restart')}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </HTMLFlipBook>
        </div>
      </div>

      {!isDone && (
        <div className="kama-lesson__nav kama-lesson__nav--sticky">
          <button
            type="button"
            className="kama-lesson__nav-prev"
            onClick={goPrev}
            disabled={index === 0 && currentGuideStep === 0}
            aria-label={t('kamaLearnMode.previousCard')}
          >
            <span className="kama-chevron" />
          </button>
          <button type="button" className="kama-lesson__nav-btn kama-lesson__nav-btn--primary" onClick={goNext}>
            {/* "Terminer" only at the very last card of the category's last
                segment — an intermediate segment's last card just opens that
                segment's test, it doesn't finish the lesson, so it keeps the
                same "Suivant →" as every other card instead of implying
                completion. */}
            {isFinalSegment && index === currentSegment.entries.length - 1 ? t('kamaLearnMode.finish') : t('kamaLearnMode.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default KamaLearnMode;
